import { GetOrganizationResponse, GetStyleResponse } from "./../App/Organization/sagas";
import { zipWith } from "ramda";
import { Action as ReduxAction, ActionFunction1, createAction } from "redux-actions";
import { call, put, select } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";
import { _getOrganizationById, _getStyleById } from "../App/Organization/sagas";

import { AxiosInterceptedResult } from "../api/interceptors";
import { INITIAL_OBJECT_AMOUNT, ObjectAmount } from "../App/actions";
import { dependencies } from "../dependencies";
import { hasErrorRecords, resolveOrganizationPostSuccessResponseValueToOrganizationUIData } from "../parsers/resolve";
import { SagaAction } from "../sagas/types";
import * as selectors from "../selectors/selectors";
import { getSkinUrls } from "../utils";
import { Creators } from "./common";
import { loadExportedSCORMPackagesAmount } from "./load-amounts";
import { Action, SagaFunction } from "./types";

export const loadAdministrableOrdIds = createAction<string>(Action.LOAD_ADMINISTRABLE_ORG_IDS);
export const handleLoadAdministrableOrdIdsSuccess = createAction<string[]>(
  Action.HANDLE_LOAD_ADMINISTRABLE_ORG_IDS_SUCCESS
);
export const handleLoadAdministrableOrdIdsFailure = createAction<{
  message: string;
}>(Action.HANDLE_LOAD_ADMINISTRABLE_ORG_IDS_FAILURE);

export interface IFetchAdministrableOrgIds {
  onFailure: ActionFunction1<{ message: string }, ReduxAction<{ message: string }>>;
  onSuccess: ActionFunction1<string[], ReduxAction<string[]>>;
}

export const fetchAdministrableOrgIds: (furtherActions: IFetchAdministrableOrgIds) => SagaFunction = fa =>
  function* _fetchAdministrableOrgIds(action: ReduxAction<string>): SagaAction {
    const { api } = dependencies;
    const userId = action.payload;
    const [err, resp]: AxiosInterceptedResult<string[]> = yield call(api.organizations.getAdministrableBy, userId);

    if (err || !resp) {
      yield put(fa.onFailure({ message: "Error fetching organizations ids" }));
      return;
    }

    yield put(fa.onSuccess(resp.data));
  };

export interface IFetchAllOrganizationsFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const fetchAllOrganizations: (
  furtherActions: IFetchAllOrganizationsFurtherActions
) => SagaFunction = furtherActions =>
  function* _fetchAllOrganizations(action: IAction): SagaAction {
    const { api } = dependencies;
    const accountId = yield select(selectors.accountId);
    const [organizationIdsError, organizationIdsResponse] = yield call(api.organizations.getAdministrableBy, accountId);

    if (organizationIdsError) {
      yield put(
        furtherActions.onFailure({
          message: "Failed to fetch organizations.",
        })
      );
    } else {
      const [organizationsError, organizationsResponse] = yield call(
        api.resolve.organization.post,
        accountId,
        organizationIdsResponse.data
      );
      if (organizationsError) {
        yield put(
          furtherActions.onFailure({
            message: "Failed to fetch organizations.",
          })
        );
      } else if (hasErrorRecords(organizationsResponse.data)) {
        yield put(
          furtherActions.onFailure({
            message: "Organizations contained errors.",
          })
        );
      } else {
        const data = zipWith(
          (uuid, datum) => ({
            ...resolveOrganizationPostSuccessResponseValueToOrganizationUIData(datum),
            organizationId: uuid,
          }),
          organizationIdsResponse.data,
          organizationsResponse.data
        );
        yield put(furtherActions.onSuccess(data));
      }
    }
  };

export interface ISelectOrganizationFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const selectOrganization: (fa: ISelectOrganizationFurtherActions) => SagaFunction = furtherActions =>
  function* _selectOrganization(action: IAction): SagaAction {
    const { window, saltcomplianceLocalStorageKeys, saltcomplianceVersion, saltcomplianceApiRoot } = dependencies;

    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.organizationId,
      action.data.organizationId
    );

    yield call(updateStyles, window, saltcomplianceApiRoot, action.data.organizationId, saltcomplianceVersion);

    const accountId: string = yield select(selectors.accountId);
    const [getOrgErr, organization]: GetOrganizationResponse = yield call(
      _getOrganizationById,
      action.data.organizationId,
      accountId
    );

    if (getOrgErr || !organization) {
      yield put(furtherActions.onFailure({ message: "Failed fetching organization" }));
      return;
    }

    let style = null;
    if (organization.style) {
      const resp: GetStyleResponse = yield call(_getStyleById, organization.style, accountId);
      style = resp[1];
    }

    yield put(
      furtherActions.onSuccess({
        ...action.data,
        style,
      })
    );
    yield put(Creators.loadClassAmount());
    yield put(Creators.loadCourseAmount());
    yield put(Creators.loadGroupsAmount());
    yield put(Creators.loadUsersAmount());
    yield put(loadExportedSCORMPackagesAmount());
  };

export async function updateStyles(
  window: any,
  root: string,
  organizationId: string,
  version: string,
  fetchTimestamp?: string
) {
  const { logo, skin } = getSkinUrls(root, organizationId, version, fetchTimestamp);

  await window.document.getElementById(logo.id).setAttribute("src", logo.url);

  await window.document.getElementById(skin.id).setAttribute("href", skin.url);
}

export const loadCourseAmount: (onLoad: ActionCreator) => SagaFunction = onLoad =>
  function* _loadCourseAmount(_: IAction): SagaAction {
    const { api, window, saltcomplianceLocalStorageKeys } = dependencies;
    const accountId = yield select(selectors.accountId);
    const orgId = yield select(selectors.organizationId);
    const getCoursesEditableByPromise = api.courses.getEditableByRelativeTo(accountId, orgId);
    yield call(
      loadObjectAmount,
      getCoursesEditableByPromise,
      saltcomplianceLocalStorageKeys.courseAmount,
      window,
      onLoad
    );
  };

export const loadClassAmount: (onLoad: ActionCreator) => SagaFunction = onLoad =>
  function* _loadClassAmount(_: IAction): SagaAction {
    const { api, window, saltcomplianceLocalStorageKeys } = dependencies;
    const accountId = yield select(selectors.accountId);
    const orgId = yield select(selectors.organizationId);
    const getClassesEditableByPromise = api.classes.getEditableByRelativeTo(accountId, orgId);
    yield call(
      loadObjectAmount,
      getClassesEditableByPromise,
      saltcomplianceLocalStorageKeys.classAmount,
      window,
      onLoad
    );
  };

export const loadExportedSCORMPackages: (onLoad: ActionCreator) => SagaFunction = onLoad =>
  function* _loadExportedSCORMPackages(_: IAction): SagaAction {
    const { api, window, saltcomplianceLocalStorageKeys } = dependencies;
    const accountId: string = yield select(selectors.accountId);
    const orgId: string = yield select(selectors.organizationId);
    const getScormPackagesEditableByPromise = api.scormPackages.getEditableByRelativeTo(accountId, orgId);
    yield call(
      loadObjectAmount,
      getScormPackagesEditableByPromise,
      saltcomplianceLocalStorageKeys.exportedScormPackagesAmount,
      window,
      onLoad
    );
  };

export const loadGroupsAmount: (onLoad: ActionCreator) => SagaFunction = onLoad =>
  function* _loadGroupsAmount(_: IAction): SagaAction {
    const { api, window, saltcomplianceLocalStorageKeys } = dependencies;
    const accountId = yield select(selectors.accountId);
    const orgId = yield select(selectors.organizationId);
    const getGroupsEditableByPromise = api.groups.getEditableByRelativeTo(accountId, orgId);
    yield call(
      loadObjectAmount,
      getGroupsEditableByPromise,
      saltcomplianceLocalStorageKeys.groupsAmount,
      window,
      onLoad
    );
  };

export const loadUsersAmount: (onLoad: ActionCreator) => SagaFunction = onLoad =>
  function* _loadUsersAmount(_: IAction): SagaAction {
    const { api, window, saltcomplianceLocalStorageKeys } = dependencies;
    const accountId = yield select(selectors.accountId);
    const orgId = yield select(selectors.organizationId);
    const getUsersEditableByPromise = api.users.getEditableByRelativeTo(accountId, orgId);
    yield call(loadObjectAmount, getUsersEditableByPromise, saltcomplianceLocalStorageKeys.usersAmount, window, onLoad);
  };

export function* loadObjectAmount(promise: Promise<any>, storageKey: string, window: Window, onLoad: ActionCreator) {
  const objectAmount = yield call(solveEditableByPromise, promise);
  yield call([window.localStorage, window.localStorage.setItem], storageKey, objectAmount);
  yield put(onLoad(objectAmount));
}

export const solveEditableByPromise = (promise: Promise<any>): Promise<string | number> =>
  new Promise(res => {
    promise.then(([err, resp]) => {
      if (err) {
        res("...");
      } else {
        const data = resp ? resp.data : [];
        res(data.length);
      }
    });
  });

export const updateCoursesAmount: (onUpdate: ActionCreator) => SagaFunction = onUpdate =>
  function* _updateCoursesAmount(action): SagaAction {
    const objectAmount: ObjectAmount = yield select(selectors.coursesAmount);
    if (objectAmount === INITIAL_OBJECT_AMOUNT) {
      return yield put(Creators.loadCourseAmount());
    }
    const { window, saltcomplianceLocalStorageKeys } = dependencies;
    const { amount } = action;
    const newCoursesAmount = `${parseInt(objectAmount as string, 0) + amount}`;
    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.courseAmount,
      newCoursesAmount
    );
    yield put(onUpdate(amount));
  };
